import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { getCachedThumb, fetchThumb } from "../lib/thumbCache";
import { formatDate } from "../lib/format";
import { downloadPdf, sediList } from "../lib/letterPdf";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { ChevronDown, ChevronRight, Folder, Home, Loader2, Paperclip, FileText, Image as ImageIcon, Film, File as FileIcon, X, List, LayoutGrid, Mail, Eye, EyeOff } from "lucide-react";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "./ui/hover-card";
import { HoverPreview } from "./AllegatiDialog";
import { useHoverPreviewPref } from "../lib/hoverPreview";
import { toast } from "sonner";
import { modSelect, noShiftTextSelect } from "../lib/mouseSelect";
import { pdfFirstPageImage } from "../lib/pdfThumb";
import * as pdfjsLib from "pdfjs-dist";
pdfjsLib.GlobalWorkerOptions.workerSrc = "https://unpkg.com/pdfjs-dist@4.0.379/build/pdf.worker.min.mjs";

const CAT_LABEL = {
  entrata: "In Entrata", uscita: "In Uscita", avvocato: "Avvocato", perito: "Perito",
  cessionario: "Cessionario", fornitore: "Fornitore", assistito: "Assistito",
};
const HOME = "__home__";
const fmtSize = (b) => { if (!b) return "0 B"; const u = ["B", "KB", "MB", "GB"]; let i = 0, n = b; while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; } return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${u[i]}`; };
const isImg = (ct) => (ct || "").startsWith("image/");
const isPdf = (ct) => (ct || "").includes("pdf");
const fileIcon = (ct, cls = "h-4 w-4 shrink-0 text-slate-400") => {
  if (isImg(ct)) return <ImageIcon className={cls} />;
  if ((ct || "").startsWith("video/")) return <Film className={cls} />;
  if (isPdf(ct)) return <FileText className={cls} />;
  return <FileIcon className={cls} />;
};

async function renderPdfThumb(blob) {
  try {
    const buf = await blob.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
    const page = await pdf.getPage(1);
    const vp = page.getViewport({ scale: 1 });
    const v = page.getViewport({ scale: 300 / vp.width });
    const canvas = document.createElement("canvas");
    canvas.width = v.width; canvas.height = v.height;
    await page.render({ canvasContext: canvas.getContext("2d"), viewport: v }).promise;
    return canvas.toDataURL("image/jpeg", 0.7);
  } catch { return null; }
}

async function renderPdfThumbFromUrl(url) {
  try {
    const pdf = await pdfjsLib.getDocument(url).promise;
    const page = await pdf.getPage(1);
    const vp = page.getViewport({ scale: 1 });
    const v = page.getViewport({ scale: 300 / vp.width });
    const canvas = document.createElement("canvas");
    canvas.width = v.width; canvas.height = v.height;
    await page.render({ canvasContext: canvas.getContext("2d"), viewport: v }).promise;
    return canvas.toDataURL("image/jpeg", 0.7);
  } catch { return null; }
}

// Miniatura: carica la thumbnail pre-generata; per i PDF non ancora migrati mostra l'originale
// via presigned (direct-to-storage) in un <object>. Altri tipi -> icona tipizzata.
function AttachmentThumb({ pid, file }) {
  const _isImg = isImg(file.content_type);
  const _isPdf = isPdf(file.content_type);
  const hasThumb = _isImg || _isPdf;
  const [src, setSrc] = useState(() => (hasThumb ? getCachedThumb(file.id) : null));
  const [failed, setFailed] = useState(false);
  const ct = file.content_type;
  useEffect(() => {
    let active = true;
    if (!hasThumb) return;
    (async () => {
      try {
        const url = await fetchThumb(pid, file.id, ct);
        if (!active) return;
        if (url) { setSrc(url); return; }
        if (_isPdf) {
          const p = await pdfFirstPageImage(`s-${file.id}`, async () => (await api.get(`/studio/pratiche/${pid}/allegati-files/${file.id}/content`, { responseType: "blob" })).data);
          if (!active) return;
          if (p) { setSrc(p); return; }
        }
        setFailed(true);
      } catch { if (active) setFailed(true); }
    })();
    return () => { active = false; };
  }, [pid, file.id, ct, hasThumb, _isPdf]);
  if (src) return <img src={src} alt={file.nome} draggable={false} className="h-full w-full object-contain pointer-events-none" />;
  if (hasThumb && !failed) return <Loader2 className="h-5 w-5 animate-spin text-slate-500" />;
  return fileIcon(ct, "h-8 w-8 text-slate-500");
}

// Avvolge un elemento con l'anteprima ingrandita al passaggio del mouse (solo immagini/PDF, se attiva).
function HoverZoom({ on, pid, file, children }) {
  if (!on || !(isImg(file.content_type) || isPdf(file.content_type))) return children;
  return (
    <HoverCard openDelay={250} closeDelay={80}>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent side="right" align="center" className="w-auto p-1 bg-slate-950 border-slate-700 shadow-2xl z-[100]" data-testid={`allega-preview-${file.id}`}>
        <HoverZoomBody pid={pid} file={file} />
      </HoverCardContent>
    </HoverCard>
  );
}

function HoverZoomBody({ pid, file }) {
  return <HoverPreview pid={pid} file={file} thumb={getCachedThumb(file.id)} />;
}

const FolderLine = ({ f, className = "" }) => (
  <div className={`flex items-center gap-1 font-bold text-sky-600 truncate ${className}`} data-testid={`allega-file-folder-${f.id}`}>
    {f.folder_id ? <Folder className="h-3 w-3 shrink-0" /> : <Home className="h-3 w-3 shrink-0" />}
    <span className="truncate">{f.folder_id ? (f.folder_name || "Cartella") : "HOME"}</span>
  </div>
);

// Selettore degli allegati già presenti nella pratica (per cartella) + lettere generate in PDF.
export default function AllegaDaPraticaDialog({ open, onOpenChange, praticaId, onConfirm, alreadySelected = [], principaleNome = "" }) {
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [letters, setLetters] = useState([]);
  const [studio, setStudio] = useState(null);
  const [sel, setSel] = useState(new Set());
  const [letterSel, setLetterSel] = useState(new Set());
  const [view, setView] = useState(() => localStorage.getItem("pgp_allega_view") || "list");
  const [generating, setGenerating] = useState(false);
  const [hoverOn, toggleHover] = useHoverPreviewPref();
  const [openSecs, setOpenSecs] = useState({});
  const changeView = (v) => { setView(v); localStorage.setItem("pgp_allega_view", v); };

  useEffect(() => {
    if (!open || !praticaId) return;
    setSel(new Set(alreadySelected)); setLetterSel(new Set()); setOpenSecs({});
    setLoading(true);
    Promise.all([
      api.get(`/studio/pratiche/${praticaId}/allegati-all`).catch(() => ({ data: { files: [] } })),
      api.get(`/studio/pratiche/${praticaId}/lettere`).catch(() => ({ data: [] })),
      api.get("/studio/me").catch(() => ({ data: null })),
    ]).then(([a, l, s]) => {
      setFiles(a.data.files || []);
      setLetters(Array.isArray(l.data) ? l.data : []);
      setStudio(s.data);
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, praticaId]);

  const sections = useMemo(() => {
    const secs = new Map();
    for (const f of files) {
      const sk = f.sezione || "principale";
      if (!secs.has(sk)) secs.set(sk, { key: sk, nome: sk === "principale" ? (principaleNome || "Cliente principale") : (f.sezione_nome || "Cliente"), map: new Map() });
      const map = secs.get(sk).map;
      const key = f.folder_id || HOME;
      const label = f.folder_id ? (f.folder_name || "Cartella") : "HOME";
      if (!map.has(key)) map.set(key, { key: `${sk}-${key}`, label, isHome: !f.folder_id, files: [] });
      map.get(key).files.push(f);
    }
    const arr = Array.from(secs.values()).map((s) => {
      const groups = Array.from(s.map.values());
      groups.sort((a, b) => (a.isHome ? -1 : b.isHome ? 1 : a.label.localeCompare(b.label)));
      return { key: s.key, nome: s.nome, groups, ids: groups.flatMap((g) => g.files.map((f) => f.id)) };
    });
    return arr.sort((a, b) => (a.key === "principale" ? -1 : b.key === "principale" ? 1 : 0));
  }, [files, principaleNome]);
  const groups = useMemo(() => sections.flatMap((s) => s.groups), [sections]);

  const toggle = (id) => { lastFile.current = id; setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; }); };
  const toggleLetter = (id) => { lastLetter.current = id; setLetterSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; }); };
  const lastFile = useRef(null);
  const lastLetter = useRef(null);
  const fileOrder = useMemo(() => groups.flatMap((g) => g.files.map((f) => f.id)), [groups]);
  const letterOrder = letters.map((l) => l.id);
  const flip = (setter) => (x) => setter((s) => { const n = new Set(s); n.has(x) ? n.delete(x) : n.add(x); return n; });
  const addAll = (setter) => (ids) => setter((s) => new Set([...s, ...ids]));
  const fileClick = (id, ids) => (e) => modSelect(e, id, ids || fileOrder, lastFile, flip(setSel), addAll(setSel));
  const letterClick = (id) => (e) => modSelect(e, id, letterOrder, lastLetter, flip(setLetterSel), addAll(setLetterSel));
  const visIds = sections.filter((s) => s.key === "principale" || openSecs[s.key]).flatMap((s) => s.ids);
  const allSel = (visIds.length + letters.length) > 0 && visIds.every((x) => sel.has(x)) && letters.every((l) => letterSel.has(l.id));
  const selectAll = () => { if (allSel) { setSel(new Set()); setLetterSel(new Set()); } else { setSel((s) => new Set([...s, ...visIds])); setLetterSel(new Set(letterOrder)); } };
  const toggleFolder = (g) => setSel((s) => {
    const n = new Set(s);
    const all = g.files.every((f) => n.has(f.id));
    g.files.forEach((f) => (all ? n.delete(f.id) : n.add(f.id)));
    return n;
  });

  const total = sel.size + letterSel.size;

  const confirm = async () => {
    const chosenFiles = files.filter((f) => sel.has(f.id));
    const chosenLetters = letters.filter((l) => letterSel.has(l.id));
    let letterAtts = [];
    if (chosenLetters.length) {
      setGenerating(true);
      try {
        const inte = sediList(studio)[0] || null;
        const pm = studio?.settings?.print_margins;
        const margins = pm && typeof pm.top === "number"
          ? { top: pm.top, right: pm.right, bottom: pm.bottom, left: pm.left }
          : { top: 20, right: 15, bottom: 15, left: 20 };
        for (const l of chosenLetters) {
          try {
            const b64 = await downloadPdf(`${l.nome || "lettera"}.pdf`, l.content || "", !!inte, inte, margins, { returnBase64: true });
            if (b64) letterAtts.push({ nome: `${(l.nome || "lettera").replace(/[^\p{L}\p{N}\-_ ]+/gu, "_").trim() || "lettera"}.pdf`, mime: "application/pdf", content_b64: b64 });
          } catch { toast.error(`PDF non generato per "${l.nome}"`); }
        }
      } finally { setGenerating(false); }
    }
    onConfirm(chosenFiles, letterAtts);
    onOpenChange(false);
  };

  const gridCls = "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-2xl max-h-[85vh] flex flex-col" data-testid="allega-da-pratica-dialog">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center justify-between gap-2 text-base pr-8">
            <span className="flex items-center gap-2"><Paperclip className="h-5 w-5 text-amber-400" /> Allega dalla pratica</span>
            {(files.length > 0 || letters.length > 0) && (
              <div className="flex items-center gap-2">
              <button type="button" onClick={toggleHover} data-testid="allega-hover-toggle" title={hoverOn ? "Disattiva anteprima ingrandita al passaggio del mouse" : "Attiva anteprima ingrandita al passaggio del mouse"}
                className={`h-8 px-2 rounded-lg border flex items-center gap-1 text-xs font-semibold transition-colors ${hoverOn ? "border-amber-500 text-amber-400 bg-amber-500/10" : "border-slate-700 text-slate-400 hover:text-slate-100"}`}>
                {hoverOn ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />} Anteprima
              </button>
              <div className="flex items-center gap-1 rounded-lg border border-slate-700 bg-slate-900/60 p-0.5" data-testid="allega-view-toggle">
                <button type="button" onClick={() => changeView("list")} title="Vista lista" data-testid="allega-view-list"
                  className={`h-7 w-7 flex items-center justify-center rounded-md transition-colors ${view === "list" ? "bg-amber-500 text-[#0a0d14]" : "text-slate-400 hover:text-slate-100"}`}><List className="h-4 w-4" /></button>
                <button type="button" onClick={() => changeView("grid")} title="Vista miniature" data-testid="allega-view-grid"
                  className={`h-7 w-7 flex items-center justify-center rounded-md transition-colors ${view === "grid" ? "bg-amber-500 text-[#0a0d14]" : "text-slate-400 hover:text-slate-100"}`}><LayoutGrid className="h-4 w-4" /></button>
              </div>
              </div>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 pr-1">
          {loading ? (
            <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>
          ) : (files.length === 0 && letters.length === 0) ? (
            <p className="text-sm text-slate-400 text-center py-8" data-testid="allega-empty">Nessun allegato o lettera presente in questa pratica.</p>
          ) : (
            <>
              <div className="flex items-center gap-3 flex-wrap" data-testid="allega-selbar">
                <button type="button" onClick={selectAll} data-testid="allega-select-all" className="h-8 px-3 rounded-md border border-slate-600 text-xs font-semibold text-slate-200 hover:bg-slate-800 inline-flex items-center gap-2">
                  <Checkbox checked={allSel} className="pointer-events-none border-slate-400 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500" tabIndex={-1} /> {allSel ? "Deseleziona tutto" : "Seleziona tutto"}
                </button>
                <span className="text-xs text-slate-300" data-testid="allega-selcount">{total} {total === 1 ? "selezionato" : "selezionati"}</span>
                <span className="hidden sm:inline text-[11px] text-slate-500">Ctrl+clic (Cmd su Mac) per scegliere più file, Maiusc+clic per un intervallo.</span>
              </div>
              {sections.map((sec) => {
                const extraSec = sec.key !== "principale";
                const tp = extraSec ? `-${sec.key}` : "";
                const secAll = sec.ids.length > 0 && sec.ids.every((x) => sel.has(x));
                const nSec = sec.ids.filter((x) => sel.has(x)).length;
                const secOpen = !extraSec || !!openSecs[sec.key];
                return (
              <div key={sec.key} data-testid={`allega-section-${extraSec ? sec.key : "principale"}`}
                className={`rounded-lg border p-2.5 space-y-3 ${extraSec ? "border-sky-500/50 bg-sky-500/[0.05]" : "border-amber-500/40 bg-amber-500/[0.03]"}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  {extraSec && (
                    <button type="button" onClick={() => setOpenSecs((o) => ({ ...o, [sec.key]: !o[sec.key] }))} data-testid={`allega-section-toggle${tp}`} aria-expanded={secOpen}
                      className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-sky-500/50 text-[11px] font-semibold text-sky-300 hover:bg-sky-500/10">
                      {secOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />} {secOpen ? "Chiudi" : "Apri"}
                    </button>
                  )}
                  <span className={`text-xs font-bold uppercase tracking-wider ${extraSec ? "text-sky-400" : "text-amber-400"}`} data-testid={`allega-section-title${tp}`}>Allegati — {sec.nome}</span>
                  <span className="text-[11px] text-slate-500">{nSec}/{sec.ids.length} selezionati</span>
                  <button type="button" onClick={() => setSel((s) => { const n = new Set(s); sec.ids.forEach((x) => (secAll ? n.delete(x) : n.add(x))); return n; })} data-testid={`allega-section-select-all${tp}`}
                    className="ml-auto h-7 px-2.5 rounded-md border border-slate-600 text-[11px] font-semibold text-slate-200 hover:bg-slate-800">{secAll ? "Deseleziona sezione" : "Seleziona sezione"}</button>
                </div>
              {secOpen && sec.groups.map((g) => {
                const allChecked = g.files.every((f) => sel.has(f.id));
                return (
                  <div key={g.key} className="rounded-lg border border-slate-800 overflow-hidden" data-testid={`allega-folder-${g.key}`}>
                    <button type="button" onClick={() => toggleFolder(g)} data-testid={`allega-folder-toggle-${g.key}`}
                      className="w-full flex items-center gap-2 px-3 py-2 bg-slate-800/50 hover:bg-slate-800 transition-colors text-left">
                      <Checkbox checked={allChecked} className="border-slate-400 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500" />
                      {g.isHome ? <Home className="h-4 w-4 text-amber-400" /> : <Folder className="h-4 w-4 text-amber-400" />}
                      <span className="text-sm font-semibold text-slate-100">{g.label}</span>
                      <span className="text-[11px] text-slate-500 ml-auto">{g.files.length} file</span>
                    </button>
                    {view === "grid" ? (
                      <div className={`${gridCls} p-2.5`}>
                        {g.files.map((f) => (
                          <label key={f.id} data-testid={`allega-file-${f.id}`} onClickCapture={fileClick(f.id, sec.ids)} onMouseDown={noShiftTextSelect}
                            className={`group relative flex flex-col rounded-lg border overflow-hidden cursor-pointer select-none transition-colors ${sel.has(f.id) ? "border-amber-500 bg-amber-500/10" : "border-slate-700 bg-slate-900/40 hover:border-slate-500"}`}>
                            <div className="absolute top-1.5 left-1.5 z-10 rounded bg-slate-900/80 p-0.5">
                              <Checkbox checked={sel.has(f.id)} onCheckedChange={() => toggle(f.id)} data-testid={`allega-file-check-${f.id}`}
                                className="border-slate-400 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500" />
                            </div>
                            <HoverZoom on={hoverOn} pid={praticaId} file={f}>
                              <div className="aspect-square flex items-center justify-center bg-slate-950/40 overflow-hidden">
                                <AttachmentThumb pid={praticaId} file={f} />
                              </div>
                            </HoverZoom>
                            <div className="px-1.5 py-1">
                              <div className="text-[11px] text-slate-200 truncate" title={f.nome}>{f.nome}</div>
                              <FolderLine f={f} className="text-[11px]" />
                              <div className="text-[10px] text-slate-500 font-mono">{fmtSize(f.size)}</div>
                            </div>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <div className="divide-y divide-slate-800/70">
                        {g.files.map((f) => (
                          <label key={f.id} data-testid={`allega-file-${f.id}`} onClickCapture={fileClick(f.id, sec.ids)} onMouseDown={noShiftTextSelect}
                            className="flex items-center gap-2.5 px-3 py-2 cursor-pointer select-none hover:bg-slate-800/40">
                            <Checkbox checked={sel.has(f.id)} onCheckedChange={() => toggle(f.id)} data-testid={`allega-file-check-${f.id}`}
                              className="border-slate-500 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500" />
                            <HoverZoom on={hoverOn} pid={praticaId} file={f}><span className="shrink-0">{fileIcon(f.content_type)}</span></HoverZoom>
                            <HoverZoom on={hoverOn} pid={praticaId} file={f}>
                              <div className="min-w-0 flex-1">
                                <div className="text-sm text-slate-200 truncate" title={f.nome}>{f.nome}</div>
                                <FolderLine f={f} className="text-xs" />
                              </div>
                            </HoverZoom>
                            {f.categoria && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700/60 text-slate-200 shrink-0">{CAT_LABEL[f.categoria] || f.categoria}</span>}
                            <span className="text-[10px] text-slate-500 shrink-0 w-16 text-right">{fmtSize(f.size)}</span>
                            <span className="text-[10px] text-slate-500 shrink-0 hidden sm:inline">{formatDate(f.created_at)}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              </div>
                );
              })}

              {letters.length > 0 && (
                <div className="rounded-lg border border-slate-800 overflow-hidden" data-testid="allega-lettere">
                  <div className="w-full flex items-center gap-2 px-3 py-2 bg-slate-800/50 text-left">
                    <Mail className="h-4 w-4 text-amber-400" />
                    <span className="text-sm font-semibold text-slate-100">Lettere generate (PDF)</span>
                    <span className="text-[11px] text-slate-500 ml-auto">{letters.length} lettere</span>
                  </div>
                  {view === "grid" ? (
                    <div className={`${gridCls} p-2.5`}>
                      {letters.map((l) => (
                        <label key={l.id} data-testid={`allega-lettera-${l.id}`} onClickCapture={letterClick(l.id)} onMouseDown={noShiftTextSelect}
                          className={`group relative flex flex-col rounded-lg border overflow-hidden cursor-pointer select-none transition-colors ${letterSel.has(l.id) ? "border-amber-500 bg-amber-500/10" : "border-slate-700 bg-slate-900/40 hover:border-slate-500"}`}>
                          <div className="absolute top-1.5 left-1.5 z-10 rounded bg-slate-900/80 p-0.5">
                            <Checkbox checked={letterSel.has(l.id)} onCheckedChange={() => toggleLetter(l.id)} data-testid={`allega-lettera-check-${l.id}`}
                              className="border-slate-400 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500" />
                          </div>
                          <div className="h-24 flex items-center justify-center bg-slate-950/40"><FileText className="h-9 w-9 text-amber-400/70" /></div>
                          <div className="px-1.5 py-1">
                            <div className="text-[11px] text-slate-200 truncate" title={l.nome}>{l.nome}</div>
                            <div className="text-[10px] text-slate-500 font-mono">{formatDate(l.created_at)}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-800/70">
                      {letters.map((l) => (
                        <label key={l.id} data-testid={`allega-lettera-${l.id}`} onClickCapture={letterClick(l.id)} onMouseDown={noShiftTextSelect}
                          className="flex items-center gap-2.5 px-3 py-2 cursor-pointer select-none hover:bg-slate-800/40">
                          <Checkbox checked={letterSel.has(l.id)} onCheckedChange={() => toggleLetter(l.id)} data-testid={`allega-lettera-check-${l.id}`}
                            className="border-slate-500 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500" />
                          <FileText className="h-4 w-4 shrink-0 text-amber-400" />
                          <span className="text-sm text-slate-200 truncate flex-1" title={l.nome}>{l.nome}</span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400 shrink-0">lettera</span>
                          <span className="text-[10px] text-slate-500 shrink-0 hidden sm:inline">{formatDate(l.created_at)}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-slate-800 pt-3">
          <span className="text-xs text-amber-300" data-testid="allega-sel-count">{total} {total === 1 ? "selezionato" : "selezionati"}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => onOpenChange(false)} data-testid="allega-cancel" className="h-9 border-slate-700 text-slate-200 hover:bg-slate-800"><X className="h-4 w-4 mr-1" /> Annulla</Button>
            <Button size="sm" onClick={confirm} disabled={total === 0 || generating} data-testid="allega-confirm" className="h-9 bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
              {generating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Paperclip className="h-4 w-4 mr-1" />} Allega {total > 0 ? `(${total})` : ""}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
